// 1,000-hour raise heads-ups on My Desk — every role (owner, 2026-10-06:
// "yes to all" on the view-differences audit B list; was owner/director).
// A manager sees only their own clinic's raises: GET /api/v1/hour-raises
// scopes to the viewer's visible clinics server-side (src/hour_raise.js
// listHourRaises) — the same list, same fields, Payroll → Manager entry
// already shows them.
//
// Reads the snapshot primed at sign-in (`hour-raises`), via `useResource` so
// this component re-renders once a decision changes it — but that hook only
// fetches a FRESH or mirrored-and-unrevalidated entry (see store.ts), and
// bootstrap already primes this key as `ready` at sign-in, so this still
// costs zero network calls on ordinary navigation (INV-1).
//
// Reject / Move buttons are OWNER ONLY (owner, 2026-09-08: "I need to be able
// to reject the raise or move it to another week" — Tomer decides raises).
// Director and manager see the fact, no controls (unchanged 2026-10-06).
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { session } from '../lib/session';
import { useResource, refetchResource } from '../lib/store';
import { authedGet, ApiError } from '../lib/apiClient';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { payPeriodLabel, type LabelledPayPeriod } from '../lib/payPeriodLabel';
import { decideHourRaise, decisionCaption, type RaiseDecisionInfo } from '../lib/hourRaises';
import { DeskPanel } from './DeskPanel';

export const HOUR_RAISES_KEY = 'hour-raises:v4';

export interface PendingRaise {
  employeeId: number;
  employeeName: string;
  fileNo: string | null;
  role: string;
  locationCode: string;
  serviceHours: number;
  currentRate: string | null;
  decision?: RaiseDecisionInfo | null;
}

export interface HourRaisesData {
  raises: PendingRaise[];
  following: {
    id: number;
    periodCode: string;
    startDate: string;
    endDate: string;
    adpWeek: number | null;
    closed: boolean;
    // Lake Mary (MELM) runs one day later than the other three clinics
    // (docs/payroll/app-period-dates-per-clinic.md) — raises here span every
    // clinic (owner/director sees all four), so each raise's own "following
    // period" line must read ITS employee's own clinic's dates out of this,
    // not the shared startDate/endDate above. See payPeriodLabel's clinic arg.
    datesByCompany?: Record<string, { startDate: string; endDate: string }>;
  } | null;
  raiseAt: number;
}

interface Period extends LabelledPayPeriod {
  id: number;
}

export function canSeeRaiseAlerts(role: string | null | undefined): boolean {
  // Raises are not a manager's (owner, 2026-10-12).
  return role === 'OWNER' || role === 'DIRECTOR';
}

function fetchHourRaises(): Promise<HourRaisesData> {
  return authedGet<HourRaisesData>('/api/v1/hour-raises');
}

export function RaiseAlerts({ chromeless = false }: { chromeless?: boolean } = {}): JSX.Element | null {
  const role = session.value.me?.role;
  const isOwner = role === 'OWNER';

  // Hooks run every render regardless of the early return below (rules of
  // hooks) — canSeeRaiseAlerts is checked again just before rendering.
  const load = useResource<HourRaisesData>(HOUR_RAISES_KEY, fetchHourRaises);
  const [action, setAction] = useState<Record<number, 'reject' | 'move' | null>>({});
  const [reason, setReason] = useState<Record<number, string>>({});
  const [moveTo, setMoveTo] = useState<Record<number, number | ''>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [rowError, setRowError] = useState<Record<number, string>>({});
  const [laterPeriods, setLaterPeriods] = useState<Period[] | null>(null);
  const [periodsFault, setPeriodsFault] = useState<string | null>(null);

  if (!canSeeRaiseAlerts(role)) return null;
  const data = load.status === 'ready' ? load.data : null;
  const raises = data?.raises ?? [];
  if (!raises.length) {
    // Outside the canvas (chromeless=false — today, only this file's own
    // dedicated tests), keep the pre-N-090c contract: nothing due, render
    // nothing. On the canvas, DeskGrid mounted this component BECAUSE it did
    // not yet know whether HOUR_RAISES_KEY had anything to say (see
    // panelCatalog.tsx's payRateAlertsHasContent / store.ts's
    // resourcePendingFetch) — returning null here would leave that mount as
    // a truly blank cell once the fetch resolves empty, so name the empty
    // state instead, same text panelCatalog.tsx's catalog entry uses for the
    // OTHER path (already-known-empty, never even mounted this component).
    if (!chromeless) return null;
    return (
      <DeskPanel title="Pay Rate" testId="raise-alerts-panel" chromeless>
        <p class="empty">No 1,000-hour raises due right now.</p>
      </DeskPanel>
    );
  }
  const following = data?.following ?? null;
  // Per-raise, not a single module-level label (bug found 2026-09-08): each
  // raise names its own employee's clinic, and Lake Mary's own dates are one
  // day later than the other three (payPeriodLabel picks them out of
  // `following.datesByCompany` when a clinic is given).
  const nextLabelFor = (locationCode: string): string => (
    following ? payPeriodLabel(following, locationCode) : 'this payroll'
  );

  async function ensureLaterPeriods(): Promise<Period[]> {
    if (laterPeriods) return laterPeriods;
    const { payPeriods } = await authedGet<{ payPeriods: Period[] }>('/api/v1/payroll/periods');
    const later = payPeriods.filter((p) => !p.closed && (!following || p.startDate > following.startDate));
    setLaterPeriods(later);
    return later;
  }

  function openReject(employeeId: number): void {
    setAction((prev) => ({ ...prev, [employeeId]: 'reject' }));
    setRowError((prev) => ({ ...prev, [employeeId]: '' }));
  }

  function openMove(employeeId: number): void {
    setAction((prev) => ({ ...prev, [employeeId]: 'move' }));
    setRowError((prev) => ({ ...prev, [employeeId]: '' }));
    setPeriodsFault(null);
    void ensureLaterPeriods().catch((err: unknown) => {
      setPeriodsFault(err instanceof Error ? err.message : String(err));
    });
  }

  function cancelAction(employeeId: number): void {
    setAction((prev) => ({ ...prev, [employeeId]: null }));
  }

  async function confirmReject(r: PendingRaise): Promise<void> {
    if (!following || busy != null) return;
    const text = (reason[r.employeeId] ?? '').trim();
    if (!text) {
      setRowError((prev) => ({ ...prev, [r.employeeId]: 'A reason is required to reject a raise.' }));
      return;
    }
    setBusy(r.employeeId);
    try {
      await decideHourRaise({
        employeeId: r.employeeId, payPeriodId: following.id, decision: 'REJECTED', reason: text,
      });
      setAction((prev) => ({ ...prev, [r.employeeId]: null }));
      await refetchResource(HOUR_RAISES_KEY, fetchHourRaises);
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [r.employeeId]: err instanceof ApiError ? err.message : String(err),
      }));
    } finally {
      setBusy(null);
    }
  }

  async function confirmMove(r: PendingRaise): Promise<void> {
    if (!following || busy != null) return;
    const target = moveTo[r.employeeId];
    if (target === '' || target === undefined) {
      setRowError((prev) => ({ ...prev, [r.employeeId]: 'Pick the pay period to move this raise to.' }));
      return;
    }
    setBusy(r.employeeId);
    try {
      await decideHourRaise({
        employeeId: r.employeeId,
        payPeriodId: following.id,
        decision: 'DEFERRED',
        deferToPeriodId: Number(target),
        reason: (reason[r.employeeId] ?? '').trim(),
      });
      setAction((prev) => ({ ...prev, [r.employeeId]: null }));
      await refetchResource(HOUR_RAISES_KEY, fetchHourRaises);
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [r.employeeId]: err instanceof ApiError ? err.message : String(err),
      }));
    } finally {
      setBusy(null);
    }
  }

  return (
    <DeskPanel
      title="Pay Rate"
      subtitle="1,000 service hours — raise due on the open payroll."
      testId="raise-alerts-panel"
      chromeless={chromeless}
    >
      <div class="queue desk-panel-scroll" data-testid="raise-alerts">
      {raises.map((r) => {
        const caption = decisionCaption(r.decision, following, r.locationCode);
        const rowAction = action[r.employeeId] ?? null;
        return (
          <div class="q raise-alert" key={r.employeeId}>
            <div class="code">{LOCATION_NAMES[r.locationCode as LocationCode] ?? r.locationCode}</div>
            <div>
              <b>{r.employeeName}</b>
              <span>
                {r.serviceHours} hours since last raise · {nextLabelFor(r.locationCode)}
              </span>
              {caption ? <span class="muted" data-testid={`raise-decision-${r.employeeId}`}>{caption}</span> : null}
            </div>
            {isOwner ? (
              <div class="raise-alert-actions" data-testid={`raise-actions-${r.employeeId}`}>
                {rowAction === null ? (
                  <>
                    <button type="button" class="btn" onClick={() => openReject(r.employeeId)}>Reject</button>
                    <button type="button" class="btn" onClick={() => openMove(r.employeeId)}>Move</button>
                  </>
                ) : rowAction === 'reject' ? (
                  <div class="raise-decision-form">
                    <input
                      type="text"
                      aria-label={`Reason to reject ${r.employeeName}'s raise`}
                      placeholder="Reason (required)"
                      value={reason[r.employeeId] ?? ''}
                      onInput={(e: Event) => {
                        const v = (e.target as HTMLInputElement).value;
                        setReason((prev) => ({ ...prev, [r.employeeId]: v }));
                      }}
                    />
                    <button
                      type="button"
                      class="btn primary"
                      disabled={busy === r.employeeId}
                      onClick={() => { void confirmReject(r); }}
                    >
                      {busy === r.employeeId ? 'Saving…' : 'Confirm reject'}
                    </button>
                    <button type="button" class="btn" onClick={() => cancelAction(r.employeeId)}>Cancel</button>
                  </div>
                ) : (
                  <div class="raise-decision-form">
                    <select
                      aria-label={`Move ${r.employeeName}'s raise to`}
                      value={moveTo[r.employeeId] === undefined ? '' : String(moveTo[r.employeeId])}
                      onChange={(e: Event) => {
                        const v = (e.target as HTMLSelectElement).value;
                        setMoveTo((prev) => ({ ...prev, [r.employeeId]: v === '' ? '' : Number(v) }));
                      }}
                    >
                      <option value="">Move to…</option>
                      {(laterPeriods ?? []).map((p) => (
                        <option key={p.id} value={p.id}>{payPeriodLabel(p, r.locationCode)}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      aria-label={`Reason to move ${r.employeeName}'s raise (optional)`}
                      placeholder="Reason (optional)"
                      value={reason[r.employeeId] ?? ''}
                      onInput={(e: Event) => {
                        const v = (e.target as HTMLInputElement).value;
                        setReason((prev) => ({ ...prev, [r.employeeId]: v }));
                      }}
                    />
                    <button
                      type="button"
                      class="btn primary"
                      disabled={busy === r.employeeId}
                      onClick={() => { void confirmMove(r); }}
                    >
                      {busy === r.employeeId ? 'Saving…' : 'Confirm move'}
                    </button>
                    <button type="button" class="btn" onClick={() => cancelAction(r.employeeId)}>Cancel</button>
                    {periodsFault ? <p class="fault">{periodsFault}</p> : null}
                  </div>
                )}
                {rowError[r.employeeId] ? <p class="fault">{rowError[r.employeeId]}</p> : null}
              </div>
            ) : null}
          </div>
        );
      })}
      </div>
    </DeskPanel>
  );
}
